import clsx from "clsx";
import React from "react";
import i18n from "../../../../../i18n";
import { useTranslation } from "react-i18next";

type BookItem = {
    id?: string | number;
    file_path: string;
    file_name: string;
    library_head_name?: string | null;
    library_author_by?: string | null;
    other_content?: (data: any) => React.ReactNode;
    other_text_content?: React.ReactNode

};

type Props = {
    baseApi: string;
    dataLike: BookItem[];
    onCancel?: (item: BookItem) => void; // กด "ยกเลิก"
    title?: string; // ชื่อหัวข้อ เช่น "หนังสือ"
    divider?: (data: any) => React.ReactNode;
    hidden_button?: boolean;
};

const fallbackImg =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='260'>
      <rect width='100%' height='100%' fill='#f2f2f2'/>
      <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#999' font-size='14'>no image</text>
    </svg>`
    );

const BookCard: React.FC<{
    item: BookItem;
    baseApi: string;
    onCancel?: (item: BookItem) => void;
    hidden_button?: boolean;
    other_content?: (data: any) => React.ReactNode;

}> = ({ item, baseApi, hidden_button, onCancel, other_content }) => {
    const { t } = useTranslation();
    const imgUrl = `${baseApi}/${item.file_path}${item.file_name}`;

    return (
        <div className="flex cursor-pointer flex-col rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex gap-3">
                <div className="w-[78px] h-[110px] shrink-0 overflow-hidden rounded-lg bg-neutral-100">
                    <img
                        src={imgUrl}
                        alt={item.file_name}
                        loading="lazy"
                        onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = fallbackImg;
                        }}
                        className="h-full w-full object-cover"
                    />
                </div>

                <div className="flex w-full justify-between items-start flex-col gap-2">
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 line-clamp-2">
                            {item.library_head_name || "-"}
                        </p>
                        <p className="mt-1 text-xs text-neutral-500 line-clamp-1">
                            {item.library_author_by || "-"}
                        </p>
                        {item.other_text_content}
                    </div>
                    {other_content && (
                        <>
                            {other_content?.(item)}
                        </>
                    )}
                    {!hidden_button && (
                        <div>
                            <button
                                type="button"
                                onClick={() => onCancel?.(item)}
                                className="w-[92px] rounded-xl border border-neutral-300 px-4 py-2 text-sm text-neutral-800 hover:bg-neutral-50 active:scale-[0.99] transition"
                            >
                                {t("ยกเลิก")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const BookLikeList: React.FC<Props> = ({
    baseApi,
    dataLike,
    onCancel,
    title = i18n.t("หนังสือ"),
    divider,
    hidden_button
}) => {
    return (
        <section className="w-full">
            <h2 className="mb-3 text-lg font-semibold text-neutral-900">{title}</h2>
            {/* responsive grid: ปรับจำนวนคอลัมน์อัตโนมัติ */}
            <div className={clsx(" gap-2", {
                'flex flex-col': !!divider,
                'grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4': !divider,
            })}>
                {dataLike?.map((item) => (
                    <div data-aos="fade-left"
                        data-aos-anchor="#example-anchor"
                        data-aos-offset="0"
                        data-aos-duration="500" className="flex flex-col gap-2">
                        <BookCard
                            key={String(item.id ?? `${item.file_path}${item.file_name}`)}
                            item={item}
                            baseApi={baseApi}
                            onCancel={onCancel}
                            hidden_button={hidden_button}
                            other_content={item.other_content}
                        />
                        {divider?.(item)}
                    </div>
                ))}
            </div>
        </section>
    );
};

export default BookLikeList;
